import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { BrandLink } from '../../_Shared/BrandLink';
import { FaAddressBook, FaBell, FaBolt, FaBook, FaBox, FaChevronDown, FaCode, FaDatabase, FaHeart, FaHistory, FaLink, FaList, FaStar, FaStickyNote, FaTerminal, FaThLarge, FaTimes } from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';
import { useScrollLock } from '../../../_Hooks/useScrollLock';

const BLOCK = 'LStudio__app_sidebar';

const SIDEBAR_ICONS = {
    terminal: FaTerminal,
    "sticky-note": FaStickyNote,
    code: FaCode,
    heart: FaHeart,
    "address-book": FaAddressBook,
    book: FaBook,
    bolt: FaBolt,
    inbox: FaBox,
    bell: FaBell,
    star: FaStar,
    history: FaHistory,
    link: FaLink,
    "th-large": FaThLarge,
    list: FaList,
    database: FaDatabase,
};

const matchesPrefix = (pathname, path) => (
    pathname === path || pathname.startsWith(`${path}/`)
);

const matchesLeaf = (pathname, item) => {
    const candidates = [item.path, ...(item.aliases ?? [])].filter(Boolean);

    if (item.match === 'prefix') {
        return candidates.some((path) => matchesPrefix(pathname, path));
    }

    return candidates.some((path) => pathname === path);
};

const isTreeItemActive = (pathname, item) => {
    const children = item.children ?? [];
    if (children.length > 0) return children.some((child) => isTreeItemActive(pathname, child));
    return matchesLeaf(pathname, item);
};

const SidebarTreeItem = ({ item, pathname, onClose, level = 0 }) => {
    const children = item.children ?? [];
    const hasChildren = children.length > 0;
    const isInside = isTreeItemActive(pathname, item);
    const [isOpen, setIsOpen] = useState(isInside || item.defaultOpen === true);

    useEffect(() => {
        if (isInside) setIsOpen(true);
    }, [isInside]);

    if (!hasChildren) {
        const isActive = matchesLeaf(pathname, item);
        const className = level === 0 ? `${BLOCK}__link` : `${BLOCK}__sublink`;
        const ItemIcon = item.icon ? SIDEBAR_ICONS[item.icon] : null;

        return (
            <Link
                to={item.path}
                onClick={onClose ?? undefined}
                className={className}
                data-level={level}
                data-kind={item.kind ?? 'leaf'}
                data-active={isActive}
                data-emphasis={item.emphasis ?? undefined}
                data-placement={item.placement ?? undefined}
                aria-current={isActive ? 'page' : undefined}
            >
                {ItemIcon && (
                    <span className={`${BLOCK}__item_icon`} aria-hidden="true">
                        <ItemIcon />
                    </span>
                )}
                <span className={`${BLOCK}__item_label`}>{item.label}</span>
            </Link>
        );
    }

    return (
        <div
            className={`${BLOCK}__group`}
            data-open={isOpen}
            data-inside={isInside}
            data-level={level}
            data-kind={item.kind ?? 'section'}
        >
            <button
                type="button"
                className={`${BLOCK}__link ${BLOCK}__group_head`}
                data-active={isInside}
                data-level={level}
                data-kind={item.kind ?? 'section'}
                aria-expanded={isOpen}
                onClick={() => setIsOpen((current) => !current)}
            >
                <span className={`${BLOCK}__group_label`}>{item.label}</span>
                <FaChevronDown className={`${BLOCK}__group_caret`} aria-hidden="true" />
            </button>

            {isOpen && (
                <div className={`${BLOCK}__sub`} data-level={level + 1}>
                    {children.map((child) => (
                        <SidebarTreeItem
                            key={child.id ?? child.path}
                            item={child}
                            pathname={pathname}
                            onClose={onClose}
                            level={level + 1}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export const SidebarPrivate = ({
    brand,
    navItems = [],
    groupLabel = 'Panel',
    homePath = '/dashboard',
    isOpen = false,
    onClose = null,
}) => {
    const SLOT = brand?.headerPrivate;
    const { pathname } = useLocation();
    const navRef = useRef(null);

    useLayoutEffect(() => {
        try {
            const saved = Number(sessionStorage.getItem('notebook.sidebar.scrollTop') || 0);
            if (navRef.current && Number.isFinite(saved)) navRef.current.scrollTop = saved;
        } catch (_) {}
    }, []);

    const persistSidebarScroll = (event) => {
        try { sessionStorage.setItem('notebook.sidebar.scrollTop', String(event.currentTarget.scrollTop || 0)); } catch (_) {}
    };

    useScrollLock(isOpen);

    useEffect(() => {
        if (!onClose) return undefined;

        const onEscape = (event) => {
            if (event.key === 'Escape') onClose();
        };

        document.addEventListener('keydown', onEscape);
        return () => document.removeEventListener('keydown', onEscape);
    }, [onClose]);

    return (
        <>
            {isOpen && <div className={`${BLOCK}__backdrop`} onClick={onClose} aria-hidden="true" />}

            <aside className={BLOCK} data-open={isOpen}>
                <div className={`${BLOCK}__sticky`}>
                    <div className={`${BLOCK}__head`}>
                        <BrandLink slot={SLOT} homePath={homePath} className={`${BLOCK}__brand`} isLogo={SLOT?.isLogo === true}>
                            {SLOT?.isLogo && SLOT?.logoLight && (
                                <img
                                    src={SLOT.logoLight}
                                    alt={SLOT?.alt ?? ''}
                                    title={SLOT?.title ?? ''}
                                    className={`${BLOCK}__logo ${BLOCK}__logo--light`}
                                    onError={(event) => { event.currentTarget.style.display = 'none'; }}
                                />
                            )}

                            {SLOT?.isLogo && SLOT?.logoDark && (
                                <img
                                    src={SLOT.logoDark}
                                    alt={SLOT?.alt ?? ''}
                                    title={SLOT?.title ?? ''}
                                    className={`${BLOCK}__logo ${BLOCK}__logo--dark`}
                                    onError={(event) => { event.currentTarget.style.display = 'none'; }}
                                />
                            )}

                            {!SLOT?.isLogo && <span className={`${BLOCK}__brand_name`}>{brand?.name}</span>}
                        </BrandLink>

                        {onClose && (
                            <button type="button" className={`${BLOCK}__close`} aria-label="Cerrar menú" onClick={onClose}>
                                <FaTimes />
                            </button>
                        )}
                    </div>

                    <nav ref={navRef} className={`${BLOCK}__nav`} aria-label="Panel" onScroll={persistSidebarScroll}>
                        {groupLabel && <p className={`${BLOCK}__title`}>{groupLabel}</p>}

                        <div className={`${BLOCK}__tree`}>
                            {navItems.map((item) => (
                                <SidebarTreeItem
                                    key={item.id ?? item.path}
                                    item={item}
                                    pathname={pathname}
                                    onClose={onClose}
                                    level={0}
                                />
                            ))}
                        </div>
                    </nav>
                </div>
            </aside>
        </>
    );
};
